import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Crown, 
  Check, 
  Star, 
  Zap, 
  Shield,
  Loader2,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Luminous background
const BACKGROUND_IMAGE = "https://customer-assets.emergentagent.com/job_e54d5963-4f37-44ed-8ca3-11e6a6219384/artifacts/4zfnbqje_IMG_3607.png";

const planIcons = {
  basic: Shield,
  pro: Zap,
  vip: Crown
};

const planColors = {
  basic: { border: "rgba(255, 215, 0, 0.3)", bg: "rgba(255, 215, 0, 0.1)" },
  pro: { border: "rgba(59, 130, 246, 0.5)", bg: "rgba(59, 130, 246, 0.1)" },
  vip: { border: "rgba(168, 85, 247, 0.5)", bg: "rgba(168, 85, 247, 0.1)" }
};

const Membership = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [email, setEmail] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/membership/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || {});
      }
    } catch (error) {
      toast.error("Failed to load membership plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) {
      toast.error("Please select a membership plan");
      return;
    }

    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/membership/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_id: selectedPlan,
          email: email.trim(),
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0500' }}>
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#0a0500' }}>
      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})`, opacity: 0.4 }}
      />
      <div 
        className="fixed inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 40%, transparent 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.8) 100%)' }}
      />

      <PublicNavbar />

      <div className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-[#FFD700]/20 text-[#FFD700] mb-4 px-4 py-1">
              <Crown size={14} className="inline mr-2" />
              MEMBERSHIP
            </Badge>
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Join the Empire
            </h1>
            <p className="text-[#E5C558] text-lg max-w-2xl mx-auto">
              Unlock exclusive access to DopeInHD's platform with our membership plans.
              Choose the tier that fits your creative journey.
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {Object.entries(plans).map(([planId, plan]) => {
              const Icon = planIcons[planId] || Star;
              const colors = planColors[planId] || planColors.basic;
              const isSelected = selectedPlan === planId;
              const isPopular = planId === "pro";
              
              return (
                <Card 
                  key={planId}
                  className={`relative cursor-pointer transition-all duration-300 hover:scale-105 ${isSelected ? 'ring-2 ring-[#FFD700]' : ''}`}
                  style={{
                    background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: `2px solid ${isSelected ? '#FFD700' : colors.border}`,
                    boxShadow: isSelected ? '0 0 40px rgba(255, 215, 0, 0.3)' : '0 0 30px rgba(255, 200, 100, 0.1)'
                  }}
                  onClick={() => handleSelectPlan(planId)}
                  data-testid={`plan-${planId}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] px-4">
                        MOST POPULAR
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pt-8">
                    <div 
                      className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                      style={{ background: colors.bg }}
                    >
                      <Icon size={32} className="text-[#FFD700]" />
                    </div>
                    <CardTitle className="text-xl text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {plan.name}
                    </CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-[#FFD700]">${plan.price}</span>
                      <span className="text-[#E5C558]">/month</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-[#E5C558]">
                          <Check size={18} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      className={`w-full mt-6 ${isSelected 
                        ? 'bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500]' 
                        : 'bg-transparent border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectPlan(planId);
                      }}
                    >
                      {isSelected ? 'Selected' : 'Select Plan'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Checkout Section */}
          {selectedPlan && (
            <Card 
              className="max-w-md mx-auto"
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                boxShadow: '0 0 50px rgba(255, 215, 0, 0.15)'
              }}
            >
              <CardHeader className="text-center">
                <CardTitle className="text-xl text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Complete Your Membership
                </CardTitle>
                <p className="text-[#E5C558] text-sm mt-2">
                  You selected: <strong className="text-[#FFD700]">{plans[selectedPlan]?.name}</strong> - ${plans[selectedPlan]?.price}/month
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#E5C558]">Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="text-[#FFFFFF]"
                    style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                  />
                  <p className="text-xs text-[#71717A] mt-1">
                    We'll send your membership confirmation here
                  </p>
                </div>
                
                <Button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold py-6"
                >
                  {processing ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
                
                <p className="text-center text-xs text-[#71717A]">
                  Secure payment powered by Stripe. Cancel anytime.
                </p>
              </CardContent>
            </Card>
          )}

          {/* FAQ Section */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-[#FFFFFF] mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
              <div className="p-6 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(255, 215, 0, 0.1)' }}>
                <h3 className="text-[#FFD700] font-semibold mb-2">Can I cancel anytime?</h3>
                <p className="text-[#E5C558] text-sm">Yes, you can cancel your membership at any time. Your access will continue until the end of your billing period.</p>
              </div>
              <div className="p-6 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(255, 215, 0, 0.1)' }}>
                <h3 className="text-[#FFD700] font-semibold mb-2">Can I upgrade my plan?</h3>
                <p className="text-[#E5C558] text-sm">Absolutely! You can upgrade to a higher tier at any time. The difference will be prorated.</p>
              </div>
              <div className="p-6 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(255, 215, 0, 0.1)' }}>
                <h3 className="text-[#FFD700] font-semibold mb-2">What payment methods do you accept?</h3>
                <p className="text-[#E5C558] text-sm">We accept all major credit cards, debit cards, and digital wallets through our secure Stripe payment system.</p>
              </div>
              <div className="p-6 rounded-lg" style={{ background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(255, 215, 0, 0.1)' }}>
                <h3 className="text-[#FFD700] font-semibold mb-2">Is there a free trial?</h3>
                <p className="text-[#E5C558] text-sm">We offer a 7-day money-back guarantee. If you're not satisfied, contact us within 7 days for a full refund.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
